import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { WebSocketProvider } from "./context/WebSocketContext";
import { TallyApprovalProvider } from "./context/TallyApprovalContext";
import BackgroundJobListener from "./components/BackgroundJobListener";
import { LookupOptionsProvider } from "./context/LookupOptionsContext";
import AppShell from "./components/AppShell";
import LoginPage from "./pages/Login";
import HomePage from "./pages/HomePage";
import { homePathForRole } from "./lib/dashboardRole";
import GateModulePage from "./pages/gate/GateModulePage";
import WeighbridgePage from "./pages/Weighbridge";
import ContractsPage from "./pages/Contracts";
import SalesContractsPage from "./pages/SalesContracts";
import OutwardDispatchPage from "./pages/OutwardDispatchPage";
import PurchasesPage from "./pages/Purchases";
import PurchaseDetailPage from "./pages/PurchaseDetail";
import LabPage from "./pages/Lab";
import StackSampleIntakePage from "./pages/StackSampleIntakePage";
import StackSampleApprovalPage from "./pages/StackSampleApprovalPage";
import UnloadingPage from "./pages/Unloading";
import StacksPage from "./pages/Stacks";
import ProductionPage from "./pages/Production";
import PackingRegisterPage from "./pages/PackingRegisterPage";
import RiceReprocessingPage from "./pages/RiceReprocessing";
import GeneralProcurementPage from "./pages/GeneralProcurement";
import StoreGRNIntakePage from "./pages/StoreGRNIntake";
import BoilerWorkspacePage from "./pages/BoilerWorkspace";
import ParboiledProductionPage from "./pages/production/ParboiledProductionPage";
import MillingProductionPage from "./pages/production/MillingProductionPage";
import ForemanTeamPage from "./pages/production/ForemanTeamPage";
import ForemanDepartmentDashboard from "./pages/production/ForemanDepartmentDashboard";
import BoilerRegisterPage from "./pages/production/BoilerRegisterPage";
import AccountsPage from "./pages/Accounts";
import TallyMappingPage from "./pages/TallyMapping";
import TallyApprovalQueuePage from "./pages/TallyApprovalQueue";
import BankReconciliationPage from "./pages/BankReconciliation";
import TdsRegisterPage from "./pages/TdsRegister";
import GeneralLedgerView from "./pages/GeneralLedgerView";
import LabourContractsPage from "./pages/LabourContracts";
import LabourAndWarehouseHub from "./pages/LabourAndWarehouseHub";
import PartiesPage from "./pages/Parties";
import PartyFormPage from "./pages/PartyFormPage";
import UsersPage from "./pages/Users";
import AuditLogsPage from "./pages/AuditLogs";
import DropdownChoicesPage from "./pages/DropdownChoices";
import PaddyQualityLabProfilesPage from "./pages/PaddyQualityLabProfiles";
import PurchaseReportsPage from "./pages/purchase/PurchaseReportsPage";
import PurchasePartyProfilePage from "./pages/purchase/PurchasePartyProfilePage";
import SalesReportsPage from "./pages/sales/SalesReportsPage";
import SalesPartyProfilePage from "./pages/sales/SalesPartyProfilePage";
import DailyMillReportPage from "./pages/DailyMillReportPage";
import ProductionBatchMastersPage from "./pages/ProductionBatchMastersPage";
import ProductionBatchesPage from "./pages/ProductionBatchesPage";
import PermissionRoute from "./components/PermissionRoute";
import AdminRoute from "./components/AdminRoute";
import { Toaster } from "./components/ui/sonner";
import PwaInstallPrompt from "./components/PwaInstallPrompt";
import PushNotificationPrompt from "./components/PushNotificationPrompt";
import BrandLogo from "./components/BrandLogo";
import "./App.css";

/** Service worker notification click → React Router navigate (when client.navigate fails). */
function PushNavigateListener() {
  const navigate = useNavigate();
  React.useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;
    const onMessage = (event) => {
      const data = event?.data;
      if (!data || data.type !== "PUSH_NAVIGATE" || !data.url) return;
      try {
        const next = new URL(data.url, window.location.origin);
        if (next.origin !== window.location.origin) return;
        navigate(`${next.pathname}${next.search}${next.hash}`);
      } catch {
        /* ignore */
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [navigate]);
  return null;
}

function DefaultRedirect() {
  const { user } = useAuth();
  return <Navigate to={homePathForRole(user?.role)} replace />;
}

function Protected() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center gradient-hero text-bansal-ivory px-6 pt-[env(safe-area-inset-top)]">
        <BrandLogo variant="onDarkCenter" />
        <p className="text-sm text-bansal-ivory/65 mt-6">Loading your workspace…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

export default function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <WebSocketProvider>
      <TallyApprovalProvider>
      <BackgroundJobListener />
      <BrowserRouter>
        <PushNavigateListener />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<Protected />}>
            <Route element={<LookupOptionsProvider><AppShell /></LookupOptionsProvider>}>
              <Route index element={<HomePage />} />
              <Route path="/daily-report" element={<PermissionRoute permission={["page.dashboard", "page.purchase_reports"]}><DailyMillReportPage /></PermissionRoute>} />
              <Route path="/gate" element={<Navigate to="/gate/procurement" replace />} />
              <Route path="/gate/procurement" element={<PermissionRoute permission="page.gate"><GateModulePage moduleKey="procurement" /></PermissionRoute>} />
              <Route path="/gate/outward" element={<PermissionRoute permission="page.gate"><GateModulePage moduleKey="outward" /></PermissionRoute>} />
              <Route path="/gate/visitors" element={<PermissionRoute permission="page.gate"><GateModulePage moduleKey="visitors" /></PermissionRoute>} />
              <Route path="/gate/store" element={<PermissionRoute permission="page.gate"><GateModulePage moduleKey="store" /></PermissionRoute>} />
              <Route path="/weighbridge" element={<Navigate to="/weighbridge/inward" replace />} />
              <Route path="/weighbridge/inward" element={<PermissionRoute permission="page.weighbridge"><WeighbridgePage moduleKey="inward" /></PermissionRoute>} />
              <Route path="/weighbridge/outward" element={<PermissionRoute permission="page.weighbridge"><WeighbridgePage moduleKey="outward" /></PermissionRoute>} />
              <Route path="/weighbridge/register" element={<PermissionRoute permission="page.weighbridge"><WeighbridgePage moduleKey="register" /></PermissionRoute>} />
              <Route path="/contracts" element={<PermissionRoute permission="page.contracts"><ContractsPage /></PermissionRoute>} />
              <Route path="/sales-contracts" element={<PermissionRoute permission="page.sales_contracts"><SalesContractsPage /></PermissionRoute>} />
              <Route path="/sales/link" element={<PermissionRoute permission="page.outward_dispatch"><OutwardDispatchPage moduleKey="link" /></PermissionRoute>} />
              <Route path="/sales/loading" element={<PermissionRoute permission="page.outward_dispatch"><OutwardDispatchPage moduleKey="loading" /></PermissionRoute>} />
              <Route path="/sales/vouchers" element={<PermissionRoute permission="page.outward_dispatch"><OutwardDispatchPage moduleKey="voucher" /></PermissionRoute>} />
              <Route path="/sales/reports" element={<PermissionRoute permission="page.sales_reports"><SalesReportsPage /></PermissionRoute>} />
              <Route path="/sales/reports/party/:id" element={<PermissionRoute permission="page.sales_reports"><SalesPartyProfilePage /></PermissionRoute>} />
              <Route path="/outward-dispatch" element={<Navigate to="/sales/link" replace />} />
              <Route path="/outward-loading" element={<Navigate to="/sales/loading" replace />} />
              <Route path="/purchases" element={<PermissionRoute permission="page.purchases"><PurchasesPage /></PermissionRoute>} />
              <Route path="/procurement/general" element={<PermissionRoute permission={["page.general_procurement", "page.purchases"]}><GeneralProcurementPage /></PermissionRoute>} />
              <Route path="/store/grn" element={<PermissionRoute permission={["page.store_keeper", "page.inventory", "page.general_procurement", "page.unloading"]}><StoreGRNIntakePage /></PermissionRoute>} />
              <Route path="/purchases/:id" element={<PermissionRoute permission="page.purchases"><PurchaseDetailPage /></PermissionRoute>} />
              <Route path="/purchase/reports" element={<PermissionRoute permission="page.purchase_reports"><PurchaseReportsPage /></PermissionRoute>} />
              <Route path="/purchase/reports/party/:id" element={<PermissionRoute permission="page.purchase_reports"><PurchasePartyProfilePage /></PermissionRoute>} />
              <Route path="/purchase/vouchers" element={<PermissionRoute permission="page.accounts"><AccountsPage defaultTab="vouchers" /></PermissionRoute>} />
              <Route path="/lab" element={<Navigate to="/lab/truck" replace />} />
              <Route path="/lab/:tab" element={<PermissionRoute permission="page.lab"><LabPage /></PermissionRoute>} />
              <Route path="/stack-sample-intake" element={<PermissionRoute permission="page.stack_samples"><StackSampleIntakePage /></PermissionRoute>} />
              <Route path="/stack-sample-approval" element={<PermissionRoute permission="page.contracts"><StackSampleApprovalPage /></PermissionRoute>} />
              <Route path="/unloading" element={<PermissionRoute permission="page.unloading"><UnloadingPage /></PermissionRoute>} />
              <Route path="/stacks" element={<PermissionRoute permission="page.stacks"><StacksPage /></PermissionRoute>} />
              <Route path="/production" element={<PermissionRoute permission="page.production"><ProductionPage /></PermissionRoute>} />
              <Route path="/production/packing-register" element={<PermissionRoute permission="page.production_packing_register"><PackingRegisterPage /></PermissionRoute>} />
              <Route path="/production/reprocessing" element={<PermissionRoute permission={["page.production_reprocessing", "page.production_packing_register"]}><RiceReprocessingPage /></PermissionRoute>} />
              <Route path="/production/batches" element={<AdminRoute><ProductionBatchesPage /></AdminRoute>} />
              <Route path="/production/batch-masters" element={<AdminRoute><ProductionBatchMastersPage /></AdminRoute>} />
              <Route path="/production/boiler/register" element={<PermissionRoute permission="page.production_boiler"><BoilerRegisterPage /></PermissionRoute>} />
              <Route path="/production/boiler/dashboard" element={<PermissionRoute permission="page.production_boiler"><ForemanDepartmentDashboard department="boiler" /></PermissionRoute>} />
              <Route path="/production/boiler" element={<PermissionRoute permission="page.production_boiler"><BoilerWorkspacePage /></PermissionRoute>} />
              <Route path="/boiler-workspace" element={<Navigate to="/production/boiler" replace />} />
              <Route path="/production/parboiled/dashboard" element={<PermissionRoute permission="page.production_parboiled"><ForemanDepartmentDashboard department="parboiled" /></PermissionRoute>} />
              <Route path="/production/parboiled" element={<PermissionRoute permission="page.production_parboiled"><ParboiledProductionPage /></PermissionRoute>} />
              <Route path="/production/milling/dashboard" element={<PermissionRoute permission="page.production_milling"><ForemanDepartmentDashboard department="milling" /></PermissionRoute>} />
              <Route path="/production/milling" element={<PermissionRoute permission="page.production_milling"><MillingProductionPage /></PermissionRoute>} />
              <Route path="/production/team" element={<PermissionRoute permission="production.foreman.team.view"><ForemanTeamPage /></PermissionRoute>} />
              <Route path="/accounts" element={<PermissionRoute permission="page.accounts"><AccountsPage /></PermissionRoute>} />
              <Route path="/accounting" element={<Navigate to="/accounts/tally-mapping" replace />} />
              <Route path="/accounting/tally-mapping" element={<Navigate to="/accounts/tally-mapping" replace />} />
              <Route path="/accounts/tally-approvals" element={<PermissionRoute permission="tally.sync.approve"><TallyApprovalQueuePage /></PermissionRoute>} />
              <Route path="/accounts/tally-mapping" element={<PermissionRoute permission="page.accounts"><TallyMappingPage /></PermissionRoute>} />
              <Route path="/accounts/tds" element={<PermissionRoute permission="page.accounts"><TdsRegisterPage /></PermissionRoute>} />
              <Route path="/accounts/bank-reconciliation" element={<PermissionRoute permission="page.accounts"><BankReconciliationPage /></PermissionRoute>} />
              <Route path="/accounts/ledger" element={<PermissionRoute permission={["page.accounts", "page.parties"]}><GeneralLedgerView /></PermissionRoute>} />
              <Route path="/labour-contracts" element={<PermissionRoute permission="page.labour_contracts"><LabourContractsPage /></PermissionRoute>} />
              <Route path="/labour-warehouse" element={<PermissionRoute permission="page.labour_warehouse"><LabourAndWarehouseHub /></PermissionRoute>} />
              <Route path="/parties" element={<PermissionRoute permission="page.parties"><PartiesPage /></PermissionRoute>} />
              <Route path="/parties/new" element={<PermissionRoute permission="page.parties"><PartyFormPage /></PermissionRoute>} />
              <Route path="/parties/:id/ledger" element={<PermissionRoute permission={["page.accounts", "page.parties"]}><GeneralLedgerView /></PermissionRoute>} />
              <Route path="/parties/:id/edit" element={<PermissionRoute permission="page.parties"><PartyFormPage /></PermissionRoute>} />
              <Route path="/users" element={<AdminRoute><UsersPage /></AdminRoute>} />
              <Route path="/audit-logs" element={<AdminRoute><AuditLogsPage /></AdminRoute>} />
              <Route path="/dropdown-choices" element={<AdminRoute><DropdownChoicesPage /></AdminRoute>} />
              <Route path="/paddy-quality-lab" element={<AdminRoute><PaddyQualityLabProfilesPage /></AdminRoute>} />
            </Route>
          </Route>
          <Route path="*" element={<DefaultRedirect />} />
        </Routes>
      <Toaster position="top-center" richColors closeButton />
      <PwaInstallPrompt />
      <PushNotificationPrompt />
      </BrowserRouter>
      </TallyApprovalProvider>
      </WebSocketProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}
